/**
 * Fill interpolation — colours and gradient ramps.
 *
 * Pure, and deliberately free of any transition machinery: nothing here knows about GSAP, the store or a timeline. `transitionManager` is one caller; a fill tween is just the arithmetic below evaluated at a progress `t`.
 *
 * A gradient is `{ type, angle, stops: [{offset, color}] }` — the shape Redux holds. Interpolating two of them needs the stop COUNTS to agree, which `matchStopCounts` arranges by resampling both ramps onto the union of their offsets, so a 2-stop fill can morph into a 5-stop one without popping.
 */

/** Parse a 6- or 8-digit hex colour to `{r, g, b, a}`. Malformed input reads as opaque black rather than throwing — a fill mid-tween must never break the frame. */
const parseHex = hex => {
	if (!hex || typeof hex !== 'string') return { r: 0, g: 0, b: 0, a: 1 }
	const h = hex.startsWith('#') ? hex.slice(1) : hex
	const r = parseInt(h.slice(0, 2), 16) || 0
	const g = parseInt(h.slice(2, 4), 16) || 0
	const b = parseInt(h.slice(4, 6), 16) || 0
	const a = h.length === 8 ? parseInt(h.slice(6, 8), 16) / 255 : 1
	return { r, g, b, a }
}

/** `{r, g, b, a}` back to a hex string — 8-digit only when alpha is partial. */
const toHex = ({ r, g, b, a }) => {
	const rr = Math.round(r).toString(16).padStart(2, '0')
	const gg = Math.round(g).toString(16).padStart(2, '0')
	const bb = Math.round(b).toString(16).padStart(2, '0')
	if (a < 1) {
		const aa = Math.round(a * 255)
			.toString(16)
			.padStart(2, '0')
		return `#${rr}${gg}${bb}${aa}`
	}
	return `#${rr}${gg}${bb}`
}

/** Lerp two hex colours, alpha included. */
export const lerpColor = (colorA, colorB, t) => {
	const a = parseHex(colorA),
		b = parseHex(colorB)
	return toHex({
		r: a.r + (b.r - a.r) * t,
		g: a.g + (b.g - a.g) * t,
		b: a.b + (b.b - a.b) * t,
		a: a.a + (b.a - a.a) * t,
	})
}

/** The colour a stop ramp shows at `offset`, clamped at both ends. */
export const sampleRamp = (stops, offset) => {
	if (offset <= stops[0].offset) return stops[0].color
	if (offset >= stops[stops.length - 1].offset) return stops[stops.length - 1].color
	for (let i = 0; i < stops.length - 1; i++) {
		if (offset >= stops[i].offset && offset <= stops[i + 1].offset) {
			const range = stops[i + 1].offset - stops[i].offset
			const t = range > 0 ? (offset - stops[i].offset) / range : 0
			return lerpColor(stops[i].color, stops[i + 1].color, t)
		}
	}
	return stops[stops.length - 1].color
}

/** Resample both ramps onto the union of their offsets, so the two arrays line up index-for-index and `interpolateReduxFill` can walk them in one pass. */
export const matchStopCounts = (stopsA, stopsB) => {
	if (stopsA.length === stopsB.length) return [stopsA, stopsB]
	const offsets = new Set()
	for (const s of stopsA) offsets.add(s.offset)
	for (const s of stopsB) offsets.add(s.offset)
	const sorted = [...offsets].sort((a, b) => a - b)
	const newA = sorted.map(offset => ({ offset, color: sampleRamp(stopsA, offset) }))
	const newB = sorted.map(offset => ({ offset, color: sampleRamp(stopsB, offset) }))
	return [newA, newB]
}

/**
 * A Redux fill value as `{type, angle, stops}`, ready to interpolate.
 *
 * A SOLID colour becomes a degenerate two-stop gradient wearing the TARGET's type and angle — that is what lets solid⇄gradient morph at all, and why the target is a parameter here.
 */
export const normalizeFill = (fill, targetFill) => {
	if (typeof fill === 'object' && fill?.stops?.length) {
		return { type: fill.type, angle: fill.angle ?? 0, stops: [...fill.stops] }
	}
	const color = typeof fill === 'string' ? fill : '#000000'
	const targetType = typeof targetFill === 'object' && targetFill?.type ? targetFill.type : 'linear'
	const targetAngle = typeof targetFill === 'object' ? (targetFill.angle ?? 0) : 0
	return {
		type: targetType,
		angle: targetAngle,
		stops: [
			{ offset: 0, color },
			{ offset: 1, color },
		],
	}
}

/** Two NORMALIZED, stop-count-matched fills at progress `t` → a Redux-style fill object. */
export const interpolateReduxFill = (from, to, t) => {
	const stops = from.stops.map((s, i) => ({
		offset: s.offset + (to.stops[i].offset - s.offset) * t,
		color: lerpColor(s.color, to.stops[i].color, t),
	}))
	const angle = from.angle + (to.angle - from.angle) * t
	return { type: from.type, angle, stops }
}

const isGradient = fill => typeof fill === 'object' && fill?.stops?.length > 0

/**
 * `{solid, at}` for a from→to fill pair. `null` for two gradients of DIFFERENT types — an instant swap is their only honest rendering.
 *
 * ⚠️ `at` is a CLOSURE: stop matching resamples both ramps and belongs at build time, while callers evaluate per frame.
 */
export const prepareFillPair = (fromFill, toFill) => {
	const fromIsGradient = isGradient(fromFill)
	const toIsGradient = isGradient(toFill)

	if (!fromIsGradient && !toIsGradient) {
		return { solid: true, at: t => lerpColor(fromFill || '#000000', toFill || '#000000', t) }
	}
	if (fromIsGradient && toIsGradient && fromFill.type !== toFill.type) return null

	const fromNorm = normalizeFill(fromFill, toFill)
	const toNorm = normalizeFill(toFill, fromFill)
	const [mFrom, mTo] = matchStopCounts(fromNorm.stops, toNorm.stops)
	fromNorm.stops = mFrom
	toNorm.stops = mTo
	return { solid: false, at: t => interpolateReduxFill(fromNorm, toNorm, t) }
}
